import React, { useState } from "react";
import Modal from "react-modal";
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  increment,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase.config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarPlus,
  faFileAlt,
  faUpload,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

const AddEventModal = ({ isOpen, onRequestClose, userId, isDarkMode }) => {
  const [eventInfo, setEventInfo] = useState({
    eventName: "",
    eventDescription: "",
    certificateImage: null,
  });
  const [uploading, setUploading] = useState(false);

  const handleEventInputChange = (e) => {
    const { name, value } = e.target;
    setEventInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleEventSubmit = async () => {
    setUploading(true);
    try {
      let certificateImageUrl = null;
      if (eventInfo.certificateImage) {
        const imageRef = ref(
          storage,
          `certificates/${eventInfo.certificateImage.name}`
        );
        await uploadBytes(imageRef, eventInfo.certificateImage);
        certificateImageUrl = await getDownloadURL(imageRef);
      }

      const eventDocRef = await addDoc(collection(db, "events"), {
        eventName: eventInfo.eventName,
        eventDescription: eventInfo.eventDescription,
        certificateImageUrl,
        userId,
      });
      const userDocRef = doc(db, "users", userId);
      await updateDoc(userDocRef, {
        eventCount: increment(1),
      });
      console.log("Document written with ID: ", eventDocRef.id);
      onRequestClose();
    } catch (error) {
      console.error("Error adding document: ", error);
    } finally {
      setUploading(false);
    }
  };

  const modalClasses = `
    modal fixed inset-0 flex items-center justify-center p-4 ${
      isDarkMode ? "bg-gray-900" : "bg-gray-100"
    }
  `;

  const contentClasses = `
    bg-gradient-to-br ${
      isDarkMode
        ? "from-gray-800 to-gray-900 text-white"
        : "from-purple-50 to-indigo-100 text-gray-900"
    }
    rounded-lg shadow-xl p-8 w-full max-w-md
  `;

  const inputClasses = `
    mt-1 block w-full rounded-md shadow-sm
    ${
      isDarkMode
        ? "bg-gray-700 border-gray-600 text-white focus:ring-purple-500 focus:border-purple-500"
        : "bg-white border-indigo-300 focus:ring-indigo-500 focus:border-indigo-500"
    }
    sm:text-sm transition-all duration-200 ease-in-out
    hover:shadow-md focus:shadow-lg
  `;

  const buttonClasses = `
    inline-flex justify-center items-center py-2 px-4 border border-transparent 
    shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 
    focus:ring-offset-2 transition-all duration-200 ease-in-out
  `;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Add Event"
      className={modalClasses}
      overlayClassName="fixed inset-0 bg-black bg-opacity-50"
      appElement={document.getElementById("root")}
    >
      <div className={contentClasses}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600">
            Add Event
          </h2>
          <button
            onClick={onRequestClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors duration-200"
          >
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </button>
        </div>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-1">
              <FontAwesomeIcon icon={faCalendarPlus} className="mr-2" />
              Event Name
            </label>
            <input
              type="text"
              name="eventName"
              value={eventInfo.eventName}
              onChange={handleEventInputChange}
              className={inputClasses}
              placeholder="Enter event name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              <FontAwesomeIcon icon={faFileAlt} className="mr-2" />
              Event Description
            </label>
            <textarea
              name="eventDescription"
              value={eventInfo.eventDescription}
              onChange={handleEventInputChange}
              className={`${inputClasses} h-32 resize-none`}
              placeholder="Describe the event"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              <FontAwesomeIcon icon={faUpload} className="mr-2" />
              Certificate Image
            </label>
            <input
              type="file"
              name="certificateImage"
              onChange={(e) =>
                setEventInfo((prev) => ({
                  ...prev,
                  certificateImage: e.target.files[0],
                }))
              }
              className={`${inputClasses} file:mr-4 file:py-2 file:px-4
                file:rounded-full file:border-0
                file:text-sm file:font-semibold
                ${
                  isDarkMode
                    ? "file:bg-purple-600 file:text-white hover:file:bg-purple-700"
                    : "file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                }
              `}
            />
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-8">
          <button
            onClick={handleEventSubmit}
            className={`${buttonClasses} ${
              uploading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
            } focus:ring-purple-500`}
            disabled={uploading}
          >
            <FontAwesomeIcon icon={faUpload} className="mr-2" />
            {uploading ? "Uploading..." : "Submit"}
          </button>
          <button
            onClick={onRequestClose}
            className={`${buttonClasses} ${
              isDarkMode
                ? "bg-gray-700 hover:bg-gray-600 text-gray-200"
                : "bg-white hover:bg-gray-100 text-gray-800 border border-gray-300"
            } focus:ring-gray-500`}
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddEventModal;